import { RockpoolAlreadyExistsModal } from '@components/rockpool/modals/RockpoolAlreadyExistsModal'
import { chainConfig, SupportedRockpoolNetworks } from '@config'
import { rockpoolService } from '@services/RockpoolService'
import { Input } from 'antd'
import { useRouter } from 'next/router'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { RockpoolItem } from '../../types/RockpoolTypes'
import { DefaultButton } from '../shared/buttons/DefaultButton'

interface SearchOpenSeaNftProps {
  chainId: number
}

export function SearchOpenSeaNft({ chainId }: SearchOpenSeaNftProps) {
  const history = useRouter()
  const [urlOpensea, setUrlOpensea] = useState('')
  const [invalidNetwork, setInvalidNetwork] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [rockpoolItem, setRockpoolItem] = useState<RockpoolItem | undefined>(undefined)

  const verifyLinkAddress = async () => {
    const config = chainConfig(chainId)

    if (!urlOpensea) {
      return
    }

    if (
      chainId !== SupportedRockpoolNetworks.ethereum &&
      chainId !== SupportedRockpoolNetworks.rinkeby &&
      chainId !== SupportedRockpoolNetworks.goerli
    ) {
      setInvalidNetwork(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(false)

    const ArrayUrl = urlOpensea.split('/')
    const collection = ArrayUrl[ArrayUrl.length - 2]
    const tokenId = ArrayUrl[ArrayUrl.length - 1]
    const networkToken = ArrayUrl[ArrayUrl.length - 3]

    if (networkToken !== config.name && ArrayUrl.indexOf('opensea.io') > -1) {
      setInvalidNetwork(true)
      setLoading(false)
      return
    }

    try {
      const searchResult = await rockpoolService().searchNft(collection, tokenId, chainId)

      if (!searchResult || (!searchResult.erc721 && !searchResult.rockpoolItem)) {
        setError(true)
        setLoading(false)
        return
      }

      if (searchResult.rockpoolItem && searchResult.rockpoolItem.isErc721Available) {
        setRockpoolItem(searchResult.rockpoolItem)
        setShowModal(true)
        setLoading(false)
        return
      }

      setLoading(false)
      history.push(`/rockpool/${chainId}/create/pool-details?collection=${collection}&tokenId=${tokenId}`)
    } catch (e) {
      setError(true)
      setLoading(false)
    }
  }

  const handleSetText = (text: string) => {
    if (text.length === 0 || error || invalidNetwork) {
      setError(false)
      setInvalidNetwork(false)
    }
    setUrlOpensea(text)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setError(false)
    setUrlOpensea('')
    const inputSearch = document.getElementById('username-input')
    inputSearch && inputSearch.focus()
  }

  return (
    <Container>
      <h1>
        Create Pools to collectively <br /> purchase NFTs from OpenSea
      </h1>
      <h2>Buy any NFT listed on OpenSea at a fixed price, together with your friends!</h2>
      <InputContainer>
        <InputSearch
          type='url'
          value={urlOpensea}
          id='username-input'
          onChange={e => handleSetText(e.target.value)}
          placeholder='Enter the URL of an OpenSea listing at a fixed price.'
          error={error}
        />
        {urlOpensea && (
          <ClearButton
            type='button'
            onClick={() => {
              setUrlOpensea('')
              setError(false)
            }}
          >
            X
          </ClearButton>
        )}
        <Button
          label='Start a Pool'
          color='primary'
          size='default'
          boldText
          disabled={!urlOpensea || invalidNetwork || error || loading}
          onClick={() => verifyLinkAddress()}
          loading={loading}
          width={166}
          height={48}
        />
      </InputContainer>
      <ButtonMobile
        label='Start a Pool'
        color='primary'
        size='default'
        boldText
        disabled={!urlOpensea || invalidNetwork || error || loading}
        onClick={() => verifyLinkAddress()}
        loading={loading}
        width={173}
        height={48}
      />
      {rockpoolItem && showModal && (
        <RockpoolAlreadyExistsModal
          rockpoolItem={rockpoolItem}
          onCancel={handleCloseModal}
          onSuccess={() =>
            history.push(
              `/rockpool/${chainId}/create/pool-details?collection=${rockpoolItem.target.collection.id}&tokenId=${rockpoolItem.target.tokenId}&listed=true`
            )
          }
        />
      )}
      {error && <Error>Invalid link. Please, provide a valid URL from OpenSea.</Error>}
      {invalidNetwork && <Error>Sorry. At the moment we only support NFTs on Ethereum. Please, try again with another one.</Error>}
    </Container>
  )
}

export const { Container, Content, InputSearch, Error, Button, ButtonMobile, InputContainer, ClearButton } = {
  Container: styled.section`
    width: 100%;
    max-width: 1204px;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 30px;

    background: transparent;
    margin: 0 auto;
    padding: 0px 0 64px;
    text-align: center;
    > h1 {
      font-style: normal;
      font-weight: 300;
      font-size: 54px;
      line-height: 120%;
      color: ${props => props.theme.colors.black};
    }
    > h2 {
      font-style: normal;
      font-weight: 300;
      font-size: 24px;
      line-height: 150%;
      color: ${props => props.theme.colors.gray[4]};
    }
    @media (max-width: ${props => props.theme.viewport.desktopXl}) {
      max-width: 100%;
      padding: 48px 24px;
      gap: 24px;

      > h1 {
        font-size: 24px;
        > br {
          display: none;
        }
      }
      > h2 {
        font-weight: 400;
        font-size: 16px;
      }
    }
  `,
  Content: styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: 10px;
    @media (max-width: ${props => props.theme.viewport.tablet}) {
      flex-direction: column;
      gap: 24px;
      align-items: center;
    }
  `,
  InputSearch: styled(Input)<{ error: boolean }>`
      background: transparent;
      border: none;
     
      &.ant-input-suffix{
        background: red;
      }
      &.ant-input-affix-wrapper{
        background: transparent;
        border: 1px solid ${props => props.theme.colors.gray[1]};
        border-radius: 8px;
        box-sizing: border-box;
      }
      &.ant-input {
        color: ${props => props.theme.colors.gray[4]};
        background: transparent;
        box-sizing: border-box;
        border-radius: 8px;
        padding: 12px 16px;
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 24px;

        ::selection {
          box-shadow: 0 0 0 0;
          border: 0 none;
          outline: 0;
        }
        
        &:focus,
        &:active,
        &:hover {
          box-shadow: 0 0 0 0;
          border: 0 none;
          outline: 0;
        }

        ${props =>
          props.error &&
          css`
            color: ${props.theme.colors.red.light};
            ::placeholder {
              color: ${props.theme.colors.red.light} !important;
            }
          `}
        ::placeholder {
          color: ${props => props.theme.colors.gray[4]};
        }
        &:focus {
          outline: none;
        }
    }
  }
  `,
  Error: styled.p`
    font-style: normal;
    font-weight: normal;
    font-size: 20px;
    line-height: 30px;
    text-align: center;
    margin-top: 10px;
    color: ${props => props.theme.colors.red.light};
  `,
  Button: styled(DefaultButton)`
    min-width: 166px;
    min-height: 48px;

    &:disabled {
      color: ${props => props.theme.colors.gray[1]};
      background: ${props => props.theme.colors.gray[2]};

      &:hover {
        color: ${props => props.theme.colors.gray[1]};
        background: ${props => props.theme.colors.gray[2]};
      }
    }

    @media (max-width: ${props => props.theme.viewport.tablet}) {
      display: none;
    }
  `,
  InputContainer: styled.div`
    width: 100%;
    height: 56px;

    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 5px 0 24px;

    background: ${props => props.theme.colors.gray[1]};
    border: 1px solid ${props => props.theme.colors.gray[1]};
    border-radius: 800px;
    box-sizing: border-box;

    @media (max-width: ${props => props.theme.viewport.tablet}) {
      padding: 0 20px;
    }

    button {
      border-radius: 800px;

      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;

      border: none;
    }
  `,
  ClearButton: styled.button`
    cursor: pointer;
    color: ${props => props.theme.colors.gray[2]};
    background: transparent;
    border-radius: 800px;

    font-style: normal;
    font-size: 16px;
    border: none;

    font-weight: 500;
    line-height: 100%;
  `,
  ButtonMobile: styled(DefaultButton)`
    display: none;

    @media (max-width: ${props => props.theme.viewport.tablet}) {
      width: 173px;
      height: 48px;

      display: flex;
      align-items: center;
      justify-content: center;

      border-radius: 800px;
      color: ${props => props.theme.colors.gray[1]};

      font-weight: 500;
      font-size: 16px;
      line-height: 100%;
    }
  `
}
